import Button from 'lib/components/button/Button';
import ErrorBag from 'lib/components/error-bag/ErrorBag';
import ProtectedComponent from 'lib/components/protected-component/ProtectedComponent';
import SearchInput from 'lib/components/search-input/SearchInput';
import Table from 'lib/components/table/Table';
import { VIEW_SETTINGS_ROLES } from 'modules/settings/settingsPermissions';
import useSettingsDynamicRules from '../../useSettingsDynamicRules';
import { stageLabel } from 'lib/dynamicModificationStages';
import DynamicStageChip from 'lib/components/dynamic-stage-chip/DynamicStageChip';
import SettingsDynamicRuleModal from '../settings-dynamic-rule-modal/SettingsDynamicRuleModal';
import SettingsDynamicRulesNav from '../settings-dynamic-rules-nav/SettingsDynamicRulesNav';

import './SettingsDynamicRulesListPage.scss';

export function StageChips({ stages = [] }) {
  return (
    <div className='dynamic-rule-stage-chips'>
      {stages.map((stage) => (
        <DynamicStageChip
          key={stage.id || stage.key || stage.stageNo}
          stage={stage}
          title={stage.name}
          label={`${stageLabel(stage)}${stage.isInitial ? ' (start)' : ''}`}
        />
      ))}
    </div>
  );
}

export default function SettingsDynamicRulesListPage(props) {
  const vm = useSettingsDynamicRules(props);

  const columns = [
    {
      title: 'Rule',
      headerClassName: 'name-cell',
      render: (rule) => (
        <div className='dynamic-rule-name'>
          <span data-testid='SettingsDynamicRulesListPage.ruleName'>{rule.name}</span>
          {rule.description && <small>{rule.description}</small>}
        </div>
      ),
    },
    {
      title: 'Stages',
      render: (rule) => <StageChips stages={rule.stages} />,
    },
    {
      title: 'Used on',
      headerClassName: 'count-cell',
      className: 'count-cell',
      render: (rule) =>
        `${rule.templatesCount} question${rule.templatesCount === 1 ? '' : 's'}`,
    },
    {
      title: '',
      headerClassName: 'actions-cell',
      className: 'actions-cell',
      render: (rule) => (
        <ProtectedComponent allowed={VIEW_SETTINGS_ROLES}>
          <div className='dynamic-rule-actions'>
            <span
              className='link-button'
              data-testid='SettingsDynamicRulesListPage.edit'
              onClick={() => vm.userOpensRule(rule)}
            >
              Edit
            </span>
            <span
              className='link-button delete'
              data-testid='SettingsDynamicRulesListPage.delete'
              onClick={() => vm.userDeletesRule(rule)}
            >
              Delete
            </span>
          </div>
        </ProtectedComponent>
      ),
    },
  ];

  return (
    <div className='settings-dynamic-rules-page settings-dynamic-rules-list-page'>
      <div className='settings-header'>
        <h4>Dynamic modification rules</h4>
      </div>
      <SettingsDynamicRulesNav />
      <p className='form-info'>
        A rule decides, for each product and factory, whether a question is asked
        in the next inspection and how strictly. Pick a rule on a question to use
        it.
      </p>
      <div className='row dynamic-rules-filter-container'>
        <div className='search-container'>
          <SearchInput
            value={vm.state.search}
            onChange={vm.setSearch}
            data-testid='SettingsDynamicRulesListPage.search'
          />
        </div>
        <ProtectedComponent allowed={VIEW_SETTINGS_ROLES}>
          <Button
            data-testid='SettingsDynamicRulesListPage.addRule'
            className='medium-button'
            onClick={vm.userOpensNewRule}
          >
            Add new rule
          </Button>
        </ProtectedComponent>
      </div>
      <ErrorBag errors={vm.state.errors} />
      <Table
        columns={columns}
        data={vm.state.data}
        className='settings-dynamic-rules-table'
        isLoading={vm.state.loading}
        page={vm.state.page}
        pageSize={vm.state.pageSize}
        count={vm.state.count}
        setPage={vm.setPage}
        setPageSize={vm.setPageSize}
        emptyState={
          <p className='empty-state'>
            No rules yet. Add one, or start from one of the examples in the form.
          </p>
        }
      />
      <SettingsDynamicRuleModal
        modal={vm.state.modal}
        onRequestClose={vm.userClosesRule}
        onChangeRule={vm.userChangesRule}
        onChangeStage={vm.userChangesStage}
        onAddStage={vm.userAddsStage}
        onRemoveStage={vm.userRemovesStage}
        onApplyPreset={vm.userAppliesPreset}
        onSave={vm.userSavesRule}
      />
    </div>
  );
}
